/**
 * The item itself, shown the way a candidate would meet it, with the review-only layer
 * added underneath rather than mixed in: patient box, stem, options, then the key,
 * the named misconceptions and the rationales.
 *
 * The reading order is deliberate. A reviewer who sees the answer first cannot judge
 * whether the item is answerable, so the key is marked in place but the reasoning sits
 * below the options where it does not colour the first read.
 */
import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ItemDetail, PATIENT_FIELDS } from '../../../inbde/reviewTypes';
import { Eyebrow, KeyChip, ProvenanceChip, FlaggedChip } from './primitives';

const PatientBox: React.FC<{ box: Record<string, string> }> = ({ box }) => (
  <div className="in-patient">
    <div
      className="in-eyebrow px-3.5 py-2"
      style={{ background: 'var(--bg-tertiary)', borderBottom: '1px solid var(--in-line)' }}
    >
      Patient
    </div>
    <div className="in-patient__grid">
      {PATIENT_FIELDS.map((f) => (
        <div key={f.key} className="in-patient__cell">
          <div className="in-eyebrow mb-1.5">{f.label}</div>
          <div className="text-[13.5px] leading-relaxed" style={{ color: 'var(--text-primary)' }}>
            {box[f.key] || <span style={{ color: 'var(--text-tertiary)' }}>Not given</span>}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ItemCard: React.FC<{ item: ItemDetail; axisALabel: string; axisBLabel: string }> = ({
  item, axisALabel, axisBLabel,
}) => {
  const reduce = useReducedMotion();
  const stem = (item.statement_blocks || [])
    .filter((b) => b.type === 'text' || b.type === 'formula')
    .map((b) => b.md || b.latex || '')
    .join('\n\n')
    .trim();

  return (
    <motion.article
      key={item.id}
      initial={reduce ? false : { opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.33, 1, 0.68, 1] }}
      className="flex flex-col gap-5"
      aria-label="Item under review"
    >
      <div className="flex flex-wrap items-center gap-2">
        <ProvenanceChip value={item.provenance} />
        {item.flagged && <FlaggedChip />}
        {item.case_size > 1 && (
          <span className="in-chip">Part of a case of {item.case_size}</span>
        )}
        {item.answered_count > 0 && (
          <span className="in-chip" title="Students have already answered this item">
            {item.answered_count} attempt{item.answered_count === 1 ? '' : 's'} recorded
          </span>
        )}
      </div>

      {item.patient_box && <PatientBox box={item.patient_box} />}

      <p
        className="text-[16px] leading-relaxed"
        style={{ color: 'var(--text-primary)', maxWidth: '62ch', textWrap: 'pretty' }}
      >
        {stem || <span style={{ color: 'var(--text-tertiary)' }}>This item has no stem text.</span>}
      </p>

      <div className="flex flex-col gap-2" style={{ maxWidth: 680 }}>
        {item.options.map((o) => (
          <div key={o.id} className={`in-option ${o.is_key ? 'in-option--key' : ''}`}>
            <span className="in-option__letter">{o.is_key ? '✓' : o.id}</span>
            <div className="flex-1 flex flex-col gap-1">
              <span
                className="text-[14.5px] leading-normal"
                style={{ color: o.is_key ? 'var(--in-correct)' : 'var(--text-primary)', fontWeight: o.is_key ? 500 : 400 }}
              >
                {o.text}
              </span>
              {o.concept_name && (
                <span className="in-mono text-[10.5px]" style={{ color: 'var(--text-tertiary)' }}>
                  {o.concept_name}
                </span>
              )}
            </div>
            {o.is_key && <KeyChip />}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-4 pt-4" style={{ borderTop: '1px solid var(--in-line)' }}>
        <Eyebrow>Why each wrong answer is wrong</Eyebrow>
        <div className="flex flex-col gap-3">
          {item.options.filter((o) => !o.is_key).map((o) => (
            <div key={o.id} className="flex gap-3">
              <span
                className="in-mono flex-none text-[11px] font-semibold px-1.5 py-0.5 h-fit rounded"
                style={{ background: 'var(--in-skipped-bg)', color: 'var(--text-secondary)' }}
              >
                {o.id}
              </span>
              <div className="flex-1 flex flex-col gap-1">
                {o.misconception ? (
                  <span className="text-[13px] leading-relaxed" style={{ color: 'var(--in-misconception)' }}>
                    {o.misconception}
                  </span>
                ) : (
                  <span className="text-[13px] leading-relaxed" style={{ color: 'var(--in-incorrect)' }}>
                    No misconception named. A student picking this gets no explanation.
                  </span>
                )}
                {o.rationale_md && (
                  <span className="text-[12.5px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    {o.rationale_md}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {item.options.find((o) => o.is_key)?.rationale_md && (
          <div className="flex flex-col gap-1.5">
            <Eyebrow>Why the keyed answer is right</Eyebrow>
            <span className="text-[13px] leading-relaxed" style={{ color: 'var(--text-primary)', maxWidth: '68ch' }}>
              {item.options.find((o) => o.is_key)?.rationale_md}
            </span>
          </div>
        )}
      </div>

      <dl
        className="grid gap-x-5 gap-y-2 pt-4 text-[12px]"
        style={{ gridTemplateColumns: 'max-content 1fr', borderTop: '1px solid var(--in-line)' }}
      >
        <dt style={{ color: 'var(--text-tertiary)' }}>{axisALabel}</dt>
        <dd style={{ color: 'var(--text-primary)' }}>{item.axis_a_label || `Area ${item.axis_a_id}`}</dd>
        <dt style={{ color: 'var(--text-tertiary)' }}>{axisBLabel}</dt>
        <dd style={{ color: 'var(--text-primary)' }}>{item.axis_b_label || `Area ${item.axis_b_id}`}</dd>
        {item.group_label && (
          <>
            <dt style={{ color: 'var(--text-tertiary)' }}>Section</dt>
            <dd style={{ color: 'var(--text-primary)' }}>{item.group_label}</dd>
          </>
        )}
        {item.task_type && (
          <>
            <dt style={{ color: 'var(--text-tertiary)' }}>Task</dt>
            <dd style={{ color: 'var(--text-primary)' }}>{item.task_type}</dd>
          </>
        )}
        <dt style={{ color: 'var(--text-tertiary)' }}>Spec</dt>
        <dd className="in-mono" style={{ color: 'var(--text-secondary)' }}>
          {item.syllabus_key} {item.spec_version}
        </dd>
      </dl>
    </motion.article>
  );
};

export default ItemCard;
